import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const sections = [
  {
    id: 'overview',
    title: '1. Overview',
    content: `HushLegal is a pre-launch legal document drafting platform. These Terms of Service apply when you reserve a founder or pre-launch spot. No product is currently available. Access is granted when the platform launches.`,
  },
  {
    id: 'not-legal-advice',
    title: '2. Not legal advice',
    content: `HushLegal is a drafting tool. Content it generates is not legal advice and does not create an attorney-client relationship with HushLegal or its operators. You are responsible for every document you generate, file, or share with clients. Read it. Verify it. Own it.\n\nCitation validation is a convenience feature. It is not a substitute for independent legal research. Always confirm citations before you file.`,
  },
  {
    id: 'reservation',
    title: '3. Reservation and billing',
    content: `When you reserve a spot, you provide a payment method through Stripe. Your card is not charged until the platform launches.\n\nAt launch, you will be charged based on your plan:\n\n• Founder: $300 billed once annually (that works out to $25 per month). The rate is locked and will not increase as long as your subscription stays active.\n\n• Pre-Launch: $79 per month, billed monthly. Cancel any time before launch at no charge.\n\nWe will send an email before any charge goes through. To cancel your reservation before launch, email billing@hushlegal.com and nothing will be charged.`,
  },
  {
    id: 'refunds',
    title: '4. Refund policy',
    content: `After launch, you have 14 days from the date of first charge to request a full refund. No explanation needed. After 14 days, the current billing period is non-refundable.\n\nTo request a refund, email billing@hushlegal.com within that window.`,
  },
  {
    id: 'data',
    title: '5. Data and privacy',
    content: `HushLegal is built on a zero data retention architecture. Client data (audio, transcriptions, generated documents) is processed in memory and destroyed. Nothing is written to our servers.\n\nFor the full picture, read our Privacy Policy. Using HushLegal means you agree to the data practices described there.`,
  },
  {
    id: 'professional',
    title: '6. Professional use',
    content: `HushLegal is for licensed attorneys and legal professionals. By reserving access, you represent that you are a licensed attorney in good standing, or a legal professional working under attorney supervision.\n\nDo not use HushLegal to generate documents in matters where you are not authorized to practice.`,
  },
  {
    id: 'availability',
    title: '7. Service availability',
    content: `HushLegal is pre-launch. We do not promise a specific launch date.\n\nIf the product has not launched within 12 months of your reservation, you will receive a full refund of any amount charged. We can modify, suspend, or remove features at any time with reasonable notice.`,
  },
  {
    id: 'liability',
    title: '8. Limitation of liability',
    content: `To the maximum extent permitted by law, HushLegal and its operators are not liable for indirect, incidental, special, consequential, or punitive damages from your use of the service (or inability to use it). This covers errors in generated documents, citation problems, and service outages.\n\nOur total liability for any claim under these Terms will not exceed what you paid us in the 12 months before the claim arose.`,
  },
  {
    id: 'governing',
    title: '9. Governing law',
    content: `These Terms are governed by Michigan law, without regard to conflict of law principles. Any dispute goes to state or federal court in Michigan.`,
  },
  {
    id: 'changes',
    title: '10. Changes to these terms',
    content: `We may update these Terms. If the changes are material, you will get an email at least 14 days before they take effect. Continued use after that date means you accept the updated Terms.`,
  },
  {
    id: 'contact',
    title: '11. Contact',
    content: `Questions about these Terms? Email legal@hushlegal.com`,
  },
];

export default function Terms() {
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    setAnimated(true);
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-[#09090b]/90 backdrop-blur border-b border-[#1a1a1c]">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/brand/logo-light.png"
              alt="HushLegal"
              className="h-8 w-auto"
            />
            <span className="text-white font-serif text-xl font-light">HushLegal</span>
          </Link>
          <Link to="/" className="text-[#a1a1aa] hover:text-white text-sm transition-colors">
            ← Back to home
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <div className={`py-16 sm:py-24 text-center transition-all duration-700 ${animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <p className="font-harvey-sans text-[#06b6d4] text-xs font-semibold tracking-widest uppercase mb-4">Legal</p>
        <h1 className="font-harvey-serif text-white text-[36px] sm:text-[52px] font-normal tracking-tight mb-4">
          Terms of Service
        </h1>
        <p className="font-harvey-sans text-[#a1a1aa] text-base font-light max-w-[600px] mx-auto leading-relaxed">
          Last updated February 2026. Read these before you reserve a spot.
        </p>
      </div>

      {/* Sections */}
      <div className="max-w-[800px] mx-auto px-6 sm:px-8 pb-24 space-y-6">
        {sections.map((section, index) => (
          <div
            key={section.id}
            id={section.id}
            className={`p-6 sm:p-8 bg-[#0f0f11] border border-[#1a1a1c] rounded-xl hover:border-[#2a2a2c] transition-all duration-500`}
            style={{ transitionDelay: `${index * 50}ms`, opacity: animated ? 1 : 0 }}
          >
            <h2 className="font-harvey-serif text-white text-xl sm:text-2xl font-normal tracking-tight mb-4">
              {section.title}
            </h2>
            <div className="space-y-3">
              {section.content.split('\n\n').map((para, i) => (
                <p key={i} className="font-harvey-sans text-[#a1a1aa] text-[15px] font-light leading-relaxed tracking-wide">
                  {para}
                </p>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="border-t border-[#1a1a1c] py-8 text-center">
        <p className="font-harvey-sans text-[#52525b] text-sm">
          © 2026 HushLegal · {' '}
          <Link to="/privacy" className="text-[#71717a] hover:text-[#06b6d4] transition-colors">Privacy Policy</Link>
          {' '} · {' '}
          <a href="mailto:legal@hushlegal.com" className="text-[#71717a] hover:text-[#06b6d4] transition-colors">legal@hushlegal.com</a>
        </p>
      </div>
    </div>
  );
}
